import { EventKey, trackEvent } from '@classic/Foundation/Analytics/GoogleTagManagerService'
import * as dataLayer from '@classic/Foundation/Analytics/AnalyticsDataLayer'
import { keys } from '@classic/Foundation/Analytics/AnalyticsDataLayer'
import { Ticket } from '@core/Areas/AccountActivity/types'
import { FollowMeShareTicket } from '../types'
import { RaceCodesLowerCase } from '@core/Areas/Racing/Types/MeetingInformation'
import { ShareBetData } from '@core/Areas/ShareBet/types'

export const trackFollowMeSortFeed = (sortItem: string): void =>
  trackFollowMeEvent({ interaction_type: 'Sort Feed', sortItem, location: getFollowMeLocation() })

export const trackFollowMeViewProfile = (): void =>
  trackFollowMeEvent({
    interaction_type: 'View Profile',
    location: getFollowMeLocation(),
    activeTab: 'Bets',
    activePill: 'Pending Shares',
  })

export const trackFollowMeToggleFollow = (activeState: 'enable' | 'disable'): void =>
  trackFollowMeEvent({
    interaction_type: 'Follow User',
    location: getFollowMeLocation(),
    activeState,
  })

export const trackFollowMeCopyBet = (
  followState: boolean,
  betType: string,
  ticket: FollowMeShareTicket
): void =>
  trackFollowMeEvent({
    interaction_type: 'Copy Bet',
    followState: followStateLabel(followState),
    betType,
    raceCode: getRaceCode(ticket),
    location: getFollowMeLocation(),
  })

export const trackFollowMeCopyBetError = (
  message: string,
  followState: boolean,
  betType: string
): void =>
  trackFollowMeEvent({
    interaction_type: 'Copy Bet Error',
    followState: followStateLabel(followState),
    message,
    betType,
    location: getFollowMeLocation(),
  })

export const trackFollowMeNotificationToggle = (activeState: 'enable' | 'disable'): void =>
  trackFollowMeEvent({
    interaction_type: 'Set Notification',
    location: getFollowMeLocation(),
    activeState,
  })

export const trackFollowMeNotificationOpened = (betType: string): void =>
  trackFollowMeEvent({
    interaction_type: 'Notification Opened',
    betType,
  })

export const trackFollowMeViewMyProfile = (): void =>
  trackFollowMeEvent({
    interaction_type: 'View My Profile',
    location: getFollowMeLocation(),
    activeTab: 'Bets',
    activePill: 'Pending Shares',
  })

export const trackFollowMeExpandSelection = (activeState: 'enable' | 'disable'): void =>
  trackFollowMeEvent({
    interaction_type: 'Expand Selection',
    location: getFollowMeLocation(),
    activeState,
  })

export const trackFollowMeProfileTabAndPillChange = (
  interaction_type: 'Tab Change' | 'Pill Change',
  activeTab: 'Bets' | 'Stats',
  activePill?: 'Pending Shares' | 'Resulted Shares' | 'My Copies'
): void =>
  trackFollowMeEvent({
    interaction_type,
    location: getFollowMeLocation(),
    activeTab,
    activePill,
  })

export const trackFollowMeNavigateToRace = (ticket: FollowMeShareTicket): void =>
  trackFollowMeEvent({
    interaction_type: 'Navigate to Race',
    location: getFollowMeLocation(),
    raceCode: getRaceCode(ticket),
  })

export const trackFollowMeShareBetBlocked = (data: ShareBetData): void => {
  let ticketType: string | undefined
  let raceCode: ShortRaceCode | undefined

  if (data.contextType === 'ticket') {
    ticketType = data.share.ticket.productName
    raceCode = getRaceCode(data.share.ticket)
  }

  if (data.contextType === 'betslip' || data.contextType === 'quickbet') {
    ticketType = data.share.items[0].bettingType
  }

  trackFollowMeEvent({
    event: keys.shareBet,
    interaction_type: 'Share Blocked',
    location: getFollowMeLocation(),
    betType: ticketType,
    destination: 'follow-me',
    raceCode,
  })
}

/** interaction_type tracking */
const interactionType = (interaction_type: string) => () => trackFollowMeEvent({ interaction_type })

export const trackFollowMeEditMyProfile = interactionType('Edit My Profile')
export const trackFollowMeEditAvatar = interactionType('Edit Avatar')
export const trackFollowMeProfileUpdated = interactionType('Profile Updated')
export const trackFollowMeRandomiseAvatar = interactionType('Randomise Avatar')
export const trackFollowMeAvatarUpdated = interactionType('Avatar Updated')

// =============
// Local Helpers
// =============

function trackFollowMeEvent({
  event = dataLayer.keys.followMe,
  interaction_type,
  sortItem,
  location,
  activeState,
  message,
  activeTab,
  activePill,
  followState,
  betType,
  raceCode,
  destination,
}: {
  event?: EventKey
  interaction_type?: string
  sortItem?: string
  location?: 'Explore' | 'Following' | 'My Profile' | 'User Profile'
  activeState?: 'enable' | 'disable'
  message?: string
  activeTab?: 'Bets' | 'Stats'
  activePill?: 'Pending Shares' | 'Resulted Shares' | 'My Copies'
  followState?: 'Following' | 'Not Following'
  betType?: string
  raceCode?: ShortRaceCode
  destination?: string
}) {
  trackEvent(event, {
    interaction_type,
    location,
    activeState,
    message,
    sortItem,
    activeTab,
    activePill,
    followState,
    betType: titleCaseSentence(betType),
    raceCode,
    destination,
  })
}

function getFollowMeLocation():
  | 'Explore'
  | 'Following'
  | 'My Profile'
  | 'User Profile'
  | undefined {
  const url = new URL(window.location.href)

  const tab = url.searchParams.get('tab')
  const pathname = url.pathname

  if (pathname === '/bet-loop') {
    if (tab === 'explore' || !tab) return 'Explore'
    if (tab === 'following') return 'Following'
  }

  if (pathname === '/bet-loop/my-profile') {
    return 'My Profile'
  }

  if (/^\/bet-loop\/profile\/[a-zA-Z0-9-]+$/.test(pathname)) {
    return 'User Profile'
  }

  return undefined
}

const followStateLabel = (isFollowing: boolean) => (isFollowing ? 'Following' : 'Not Following')

const getRaceCode = (ticket: Ticket | FollowMeShareTicket): ShortRaceCode | undefined => {
  const raceCodes: Record<string, ShortRaceCode> = {
    Greyhounds: 'dogs',
    Racing: 'races',
    Trots: 'trots',
  }
  const shortCode = (code: string) => raceCodes[code] ?? undefined

  const legs = ticket.legs

  if (legs.length === 0) return undefined

  if (legs.length === 1 && legs[0].eventType) {
    return shortCode(legs[0].eventType)
  }

  const firstRaceCode = legs[0].eventType
  const isMixed = legs.some(leg => leg.eventType !== firstRaceCode)

  return isMixed ? 'combination' : firstRaceCode ? shortCode(firstRaceCode) : undefined
}

const titleCaseSentence = (sentence: string | undefined): string | undefined => {
  if (!sentence) return

  return sentence
    .split(' ')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ')
}

// =====
// Types
// =====

type ShortRaceCode = RaceCodesLowerCase | 'combination'
